import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Detect if API is same-origin (cookies work) or cross-origin (need Bearer tokens)
const isSameOrigin = (() => {
  try {
    const apiUrl = new URL(BACKEND_URL);
    return apiUrl.origin === window.location.origin;
  } catch {
    return true;
  }
})();

// Only enable withCredentials for same-origin (avoids CORS preflight issues on custom domains)
axios.defaults.withCredentials = isSameOrigin;

// Restore token from localStorage on page load
const savedToken = localStorage.getItem('auth_token');
if (savedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
}

// Helper to persist token and set Authorization header
const persistToken = (token) => {
  if (token) {
    localStorage.setItem('auth_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

const clearToken = () => {
  localStorage.removeItem('auth_token');
  delete axios.defaults.headers.common['Authorization'];
};

// ─── User persistence ─────────────────────────────────────────────────
// Persist last-known user to localStorage so cold-start network failures
// (429 / 5xx) don't leave the app stranded with user=null. The token is
// the source of truth for "is logged in"; the cached user is just a hint.
const USER_CACHE_KEY = 'auth_user_v1';

const persistUser = (userData) => {
  try {
    if (userData) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch {}
};

const readPersistedUser = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// ─── Global axios response interceptor ────────────────────────────────
// Single source of truth for auth-related HTTP error handling.
// Only treats 401/403 as auth failures. 429 (rate limit) and 5xx
// pass through untouched so the user is NOT logged out on throttle.
let interceptorInstalled = false;
const installInterceptor = (onAuthFailure) => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';
      const isApiCall = url.includes('/api/');
      const isAuthEndpoint = url.includes('/auth/login') ||
                             url.includes('/auth/register') ||
                             url.includes('/auth/logout');
      if (isApiCall && !isAuthEndpoint && (status === 401 || status === 403)) {
        if (localStorage.getItem('auth_token')) {
          clearToken();
          persistUser(null);
          onAuthFailure?.();
        }
      }
      return Promise.reject(error);
    }
  );
};

// ─── /auth/me micro-cache ─────────────────────────────────────────────
// Avoids repeated network calls during rapid navigation. 30s window.
const ME_CACHE_TTL_MS = 30_000;
let meCache = { data: null, expiresAt: 0, inflight: null };

const fetchMeCached = async (force = false) => {
  const now = Date.now();
  if (!force && meCache.data && meCache.expiresAt > now) {
    return meCache.data;
  }
  if (meCache.inflight) {
    return meCache.inflight;
  }
  meCache.inflight = axios.get(`${API}/auth/me`)
    .then((response) => {
      meCache.data = response.data;
      meCache.expiresAt = Date.now() + ME_CACHE_TTL_MS;
      meCache.inflight = null;
      return response.data;
    })
    .catch((err) => {
      meCache.inflight = null;
      throw err;
    });
  return meCache.inflight;
};

const invalidateMeCache = () => {
  meCache = { data: null, expiresAt: 0, inflight: null };
};

export const AuthProvider = ({ children }) => {
  // Rehydrate from localStorage on cold start so transient 429/5xx
  // doesn't leave the app stranded with user=null.
  const [user, setUser] = useState(() => {
    if (savedToken) return readPersistedUser();
    return null;
  });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  // Install global interceptor once + listen for fetch-based auth clearings
  useEffect(() => {
    installInterceptor(() => {
      if (mountedRef.current) setUser(null);
      invalidateMeCache();
    });
    const onAuthCleared = () => {
      if (mountedRef.current) setUser(null);
      invalidateMeCache();
    };
    window.addEventListener('auth:cleared', onAuthCleared);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('auth:cleared', onAuthCleared);
    };
  }, []);

  const checkAuth = useCallback(async (force = false) => {
    // Skip the call entirely on public pages where there's no token —
    // avoids 401 console noise for anonymous visitors on marketing pages.
    if (!localStorage.getItem('auth_token')) {
      setLoading(false);
      return;
    }
    try {
      const data = await fetchMeCached(force);
      if (data && data.email) {
        setUser(data);
        persistUser(data);
      }
    } catch (err) {
      // Only clear auth on real auth errors (401/403). Transient errors
      // like 429 (rate limit) or 5xx should NOT log the user out.
      // The interceptor already handles token clearing for 401/403.
      // For 429/5xx with a token still present, we trust the persisted
      // user (already rehydrated above) and do nothing.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setUser(null);
        persistUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Traditional email/password login
  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });

    // Handle 2FA challenge
    if (response.data.requires_2fa) {
      return { requires_2fa: true, user_id: response.data.user_id, email_hint: response.data.email_hint, fallback_code: response.data.fallback_code };
    }

    const { user: userData, access_token } = response.data;
    persistToken(access_token);
    invalidateMeCache();
    setUser(userData);
    persistUser(userData);
    return userData;
  };

  // Complete 2FA login
  const verify2faLogin = async (userId, code) => {
    const response = await axios.post(`${API}/auth/login/2fa-verify`, { user_id: userId, code });
    const { user: userData, access_token } = response.data;
    persistToken(access_token);
    invalidateMeCache();
    setUser(userData);
    persistUser(userData);
    return userData;
  };

  // Refresh user data from backend (forces cache miss)
  const refreshUser = async () => {
    try {
      const data = await fetchMeCached(true);
      setUser(data);
      persistUser(data);
    } catch (e) {
      console.error('Refresh user failed:', e);
    }
  };

  // Traditional email/password registration
  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { user: userData, access_token } = response.data;
    persistToken(access_token);
    invalidateMeCache();
    setUser(userData);
    persistUser(userData);
    return userData;
  };

  // Set user from Google OAuth callback
  const setGoogleUser = (userData) => {
    invalidateMeCache();
    setUser(userData);
    persistUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearToken();
      persistUser(null);
      invalidateMeCache();
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login,
      verify2faLogin,
      register, 
      logout, 
      setGoogleUser,
      refreshUser,
      checkAuth 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
